import React, { useRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography, message } from 'antd';
import type { RootState, AppDispatch } from '@@store/index';
import type { FooterButton } from '@@store/slices/chatSlice';
import { sendMessage, addMessage, setIsOpen, setFooterButtonsHistoryEntry, setActiveForm } from '@@store/slices/chatSlice';
import { useDevice } from '@/hooks';
import { UserInput } from '@/base-components';
import { UserItem, BotItem, DamageForm } from './chat-components';
import './index.less';
import { getTimeoutStrategy, getTimeoutCloseStrategy, closeSession } from '@@service/api';

const { Title, Text } = Typography;

const TIMEOUT_FOLLOWUP_BUTTONS: FooterButton[] = [
    {
        label: 'Issue resolved',
        event: {
            type: 'timeout_action',
            action: 'resolved'
        }
    },
    {
        label: 'Need more help',
        event: {
            type: 'timeout_action',
            action: 'more_help',
            reply_text: 'Continue'
        }
    }
];

const ChatPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const {
        messages,
        isLoading,
        currentSessionId,
        error,
        status,
        streamingAssistantMessage,
        streamingBubbleLoading,
        currentMessageType,
        isOpen
    } = useSelector((state: RootState) => state.chat);
    const curTheme = useSelector((state: RootState) => state.theme);

    const { isMobile } = useDevice();
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const streamingSegments = useSelector((state: RootState) => state.chat.streamingSegments);
    const activeForm = useSelector((state: RootState) => state.chat.activeForm);

    // 本地调试触发（/adp 或 damage form 输入）
    const [ damageFormOpen, setDamageFormOpen ] = useState(false);

    // 后端推送的表单：存在即渲染
    const isBackendForm = !!activeForm;
    const formOpen = isBackendForm || damageFormOpen;
    const formPrefill = (activeForm?.prefill || undefined) as any;

    // 自动滚动到底部
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [ messages, status, streamingAssistantMessage, streamingBubbleLoading, streamingSegments.length ]);

    // 显示错误信息
    useEffect(() => {
        if (error) {
            message.error(error);
        }
    }, [ error ]);

    // 超时以用户最后发消息为准
    useEffect(() => {
        if (!currentSessionId || messages.length === 0) return;
        if (!isOpen) return;
        let timeoutWarnTimer: NodeJS.Timeout | null = null;
        let timeoutCloseTimer: NodeJS.Timeout | null = null;
        const lastUserMsg = [ ...messages ].reverse().find(msg => msg.role === 'user');
        if (!lastUserMsg) return;
        const hasClosed = messages.some(msg => msg.id && msg.id.startsWith('timeout-close-'));
        const userMsgTime = lastUserMsg.timestamp;
        const now = Date.now();
        const warnDelay = 10 * 60 * 1000;
        const closeDelay = 30 * 60 * 1000;
        const elapsed = now - userMsgTime;
        const hasWarnAfterUser = messages.some(msg => (
            msg.id && msg.id.startsWith('timeout-warn-') && msg.timestamp >= userMsgTime
        ));
        // 流式时不做任何定时
        if (status === 'streaming') return;
        // 补偿逻辑：若已过warnDelay但未closeDelay，且还没发过warn，本轮立即补warn；否则常规定时
        const pushTimeoutReminder = async () => {
            const response = await getTimeoutStrategy();
            const text = response?.message || 'Are you still there?';
            const messageId = `timeout-warn-${Date.now()}`;
            dispatch(addMessage({
                id: messageId,
                content: text,
                role: 'assistant',
                timestamp: Date.now()
            }));
            if (response?.button) {
                dispatch(setFooterButtonsHistoryEntry({
                    messageId,
                    buttons: TIMEOUT_FOLLOWUP_BUTTONS.map((btn) => ({
                        ...btn,
                        event: { ...btn.event }
                    }))
                }));
            }
        };

        if (!hasWarnAfterUser && elapsed >= warnDelay && elapsed < closeDelay) {
            (async () => {
                await pushTimeoutReminder();
            })();
        } else if (elapsed < warnDelay) {
            timeoutWarnTimer = setTimeout(async () => {
                await pushTimeoutReminder();
            }, warnDelay - elapsed);
        }
        // closeDelay逻辑（未关闭时都要排队）
        if (!hasClosed && elapsed < closeDelay) {
            timeoutCloseTimer = setTimeout(async () => {
                const text = await getTimeoutCloseStrategy();
                dispatch(addMessage({
                    id: `timeout-close-${Date.now()}`,
                    content: text || 'Due to your long period of inactivity in the session, the system will close this conversation.',
                    role: 'assistant',
                    timestamp: Date.now()
                }));
                await closeSession();
                dispatch(setIsOpen(false));
            }, closeDelay - elapsed);
        }
        return () => {
            if (timeoutWarnTimer) clearTimeout(timeoutWarnTimer);
            if (timeoutCloseTimer) clearTimeout(timeoutCloseTimer);
        };
    }, [ messages, currentSessionId, dispatch, isOpen, status ]);

    const handleSend = async (value: string, attachments?: string[]) => {
        if (!value.trim() && (!attachments || attachments.length === 0)) return;

        // 本地触发：输入 "/adp" 或 "damage/demage form" 时打开 ADP 表单抽屉（不发送到后端）
        if (/^\s*(\/adp|d[ae]mage\s*form)\s*$/i.test(value)) {
            setDamageFormOpen(true);
            return;
        }

        if (!currentSessionId) {
            message.error('No active conversation. Please create a new chat first.');
            return;
        }

        try {
            await dispatch(sendMessage({
                content: value,
                attachments
            })).unwrap();

        } catch (error) {
            message.error('Failed to send message. Please try again.');
        }
    };


    return (
        <div className={`chat-page-layout ${curTheme}-chat-page ${isMobile ? 'mobile-chat-page' : 'pc-chat-page'}`}>
            <h1 className="visually-hidden">Agentic Intelligent Resolver Chat</h1>
            {/* Header */}
            {
                !isMobile && (
                    <div className="chat-page-header">
                        <Title level={2} className="header-title">AIR</Title>
                        {/* 测试期间展示的SessionId，正式时隐藏 */}
                        {/* <span>SessionId: {currentSessionId}</span> */}
                    </div>
                )
            }

            {/* chat Content */}
            <div className="chat-page-content">
                <div
                    className="messages-container custom-scrollbar"
                    tabIndex={0}
                    role="region"
                    aria-label="Chat messages"
                >
                    {messages.map((msg) => (
                        <div key={msg.id} className="message-wrapper">
                            {msg.role === 'user' ? (
                                <UserItem
                                    content={msg.content}
                                    attachments={msg.attachments}
                                />
                            ) : (
                                <BotItem
                                    content={msg.content}
                                    type={msg.type}
                                    messageId={msg.id}
                                    topics={msg.topics}
                                />
                            )}
                        </div>
                    ))}

                    {/* 优化后的streaming气泡渲染逻辑 */}
                    {status === 'streaming' && (
                        <div className="message-wrapper">
                            <BotItem
                                content={streamingAssistantMessage}
                                isStreaming={streamingBubbleLoading}
                                type={currentMessageType}
                            />
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>
                <div className="sender-container">
                    <UserInput
                        onSubmit={handleSend}
                        loading={isLoading || status === 'streaming' || status === 'submitted'}
                        disabled={!currentSessionId || !isOpen || status === 'streaming' || status === 'submitted'}
                        maxLength={400}
                        maxFileCount={1}
                    />
                </div>
            </div>

            <DamageForm
                open={formOpen}
                prefill={formPrefill}
                title={activeForm?.title}
                description={activeForm?.description}
                readonlyFields={activeForm?.readonly_fields}
                requiredFields={activeForm?.required_fields}
                agreementRequired={activeForm?.agreement_required}
                agreementText={activeForm?.agreement_text}
                cancelConfirmText={activeForm?.cancel_confirm_text}
                onClose={() => {
                    if (isBackendForm && activeForm) {
                        dispatch(sendMessage({
                            formSubmit: {
                                form_id: activeForm.form_id,
                                form_type: activeForm.form_type,
                                status: 'cancelled'
                            }
                        }));
                        dispatch(setActiveForm(null));
                    } else {
                        setDamageFormOpen(false);
                    }
                }}
                onSave={(values) => {
                    if (isBackendForm && activeForm) {
                        dispatch(sendMessage({
                            formSubmit: {
                                form_id: activeForm.form_id,
                                form_type: activeForm.form_type,
                                status: 'submitted',
                                values
                            }
                        }));
                        dispatch(setActiveForm(null));
                    } else {
                        // 本地调试：仅打印
                        // eslint-disable-next-line no-console
                        console.log('[DamageForm] saved', values);
                        setDamageFormOpen(false);
                    }
                }}
            />

            {/* Bottom */}
            {
                !isMobile && (
                    <div className="chat-page-bottom">
                        <Text type="secondary" className="disclaimer-text">
                            AI-generated content — please verify before use.
                        </Text>
                    </div>
                )
            }
        </div>
    );
};

export default ChatPage;